import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Cross-browser stable header drawer (iOS + Android + Desktop)
 */
class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-drawer-toggle]');
    this.container = this.querySelector('[data-drawer-container]');
    this.drawer = this.container?.querySelector('.menu-drawer');
    this.backdrop = this.container?.querySelector('[data-drawer-backdrop]');
    this.icon = this.toggleBtn?.querySelector('.dot-square');
    this.scrollPosition = 0;

    this.toggleBtn?.addEventListener('click', () => this.toggle());
    this.backdrop?.addEventListener('click', () => this.close());
    document.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
  }

  get isOpen() {
    return this.container.classList.contains('menu-open');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (this.isOpen) return;

    this.scrollPosition = window.scrollY;

    // ✅ Use HTML scroll lock (iOS-safe)
    document.documentElement.setAttribute('scroll-locked', '');
    document.body.dataset.scrollY = this.scrollPosition;

    // ✅ Add open state
    this.container.classList.add('menu-open');
    this.container.setAttribute('open', '');
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    this.icon?.classList.add('is-active');

    // ✅ Focus trap + hide announcement bar
    trapFocus(this.drawer);
    this.#toggleAnnouncementBar(false);

    // ✅ Smooth scroll lock (prevents bounce)
    document.body.style.overscrollBehaviorY = 'contain';
    document.body.style.touchAction = 'none';
  }

  close() {
    if (!this.isOpen) return;

    // ✅ Remove open state
    this.container.classList.remove('menu-open');
    this.container.removeAttribute('open');
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    this.icon?.classList.remove('is-active');

    removeTrapFocus();
    this.#toggleAnnouncementBar(true);

    const y = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.removeAttribute('data-scroll-y');
    document.documentElement.removeAttribute('scroll-locked');

    document.body.style.overscrollBehaviorY = '';
    document.body.style.touchAction = '';

    // ✅ Force repaint and restore scroll
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: 'instant' });

      const header = document.querySelector('header-component');
      if (header && typeof header.refresh === 'function') {
        header.refresh();
      } else {
        window.dispatchEvent(new Event('scroll'));
      }
    });
  }

  #toggleAnnouncementBar(enable) {
    const bar = document.querySelector(
      '.shopify-section-announcement-bar, .announcement-bar, .section-announcement-bar'
    );
    if (!bar) return;
    if (enable) {
      bar.style.display = '';
      bar.removeAttribute('aria-hidden');
    } else {
      bar.style.display = 'none';
      bar.setAttribute('aria-hidden', 'true');
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
